import {
    useInfiniteQuery,
    UseInfiniteQueryOptions,
    useMutation,
    UseMutationOptions,
    useQuery,
    UseQueryOptions
} from '@tanstack/react-query'
import { useAuth0 } from './auth'
import { z } from 'zod'

const API_BASE_URL = import.meta.env.VITE_API_URL ?? ''

export class ApiError extends Error {
    status: number
    constructor(message: string, status: number) {
        super(message)
        this.status = status
    }
}

/**
 * Single source of truth for an authenticated request: bearer auth, JSON body on POST,
 * `ApiError` (with status) on a non-OK response, and zod parsing of the body. The hooks below
 * delegate here, and it's also callable outside React (e.g. an export that pages the API from a
 * callback, where the query hooks can't be used).
 */
export const apiRequest = async <TSchema extends z.ZodType>(
    method: 'GET' | 'POST',
    path: string,
    schema: TSchema,
    accessToken: string,
    body?: unknown
): Promise<z.infer<TSchema>> => {
    const isPost = method === 'POST'
    const response = await fetch(`${API_BASE_URL}${path}`, {
        method,
        headers: {
            Authorization: `Bearer ${accessToken}`,
            ...(isPost && { 'Content-Type': 'application/json' })
        },
        ...(isPost && { body: JSON.stringify(body) })
    })

    if (!response.ok) {
        const errorBody = await response.json().catch(() => null)
        throw new ApiError(errorBody?.message ?? `Request failed: ${response.statusText}`, response.status)
    }

    return schema.parse(await response.json())
}

export const useApiMutation = <TBody, TSchema extends z.ZodType>(
    path: string,
    schema: TSchema,
    options?: Omit<UseMutationOptions<z.infer<TSchema>, Error, TBody>, 'mutationFn'>
) => {
    const { getAuthenticationData } = useAuth0()

    return useMutation<z.infer<TSchema>, Error, TBody>({
        ...options,
        mutationFn: async (body: TBody) => {
            const { accessToken } = await getAuthenticationData()
            return apiRequest('POST', path, schema, accessToken, body)
        }
    })
}

/**
 * Cursor-paginated POST endpoint. `buildBody(pageParam)` is called per page and
 * should embed the cursor (when defined) into the request body. The caller's
 * `options.getNextPageParam` returns the next cursor, or `undefined` when done.
 */
export const useApiInfinite = <TSchema extends z.ZodType, TPageParam>(
    path: string,
    buildBody: (pageParam: TPageParam | undefined) => unknown,
    schema: TSchema,
    options: Omit<UseInfiniteQueryOptions<z.infer<TSchema>, Error>, 'queryFn'>
) => {
    const { getAuthenticationData } = useAuth0()

    return useInfiniteQuery<z.infer<TSchema>, Error>({
        ...options,
        queryFn: async ({ pageParam }) => {
            const { accessToken } = await getAuthenticationData()
            return apiRequest('POST', path, schema, accessToken, buildBody(pageParam as TPageParam | undefined))
        }
    })
}

export const useApi = <TSchema extends z.ZodType>(
    path: string,
    body: unknown,
    schema: TSchema,
    options: Omit<UseQueryOptions<z.infer<TSchema>, Error>, 'queryFn'>,
    method: 'GET' | 'POST' = 'POST'
) => {
    const { getAuthenticationData } = useAuth0()

    return useQuery<z.infer<TSchema>, Error>({
        ...options,
        queryFn: async () => {
            const { accessToken } = await getAuthenticationData()
            return apiRequest(method, path, schema, accessToken, method === 'POST' ? body : undefined)
        }
    })
}
